import React from 'react'
import { List } from '@fs/zion-ui'
import { type GroupSummary } from '@fs/zion-tree-types'
import { type GedcomFileFailed, type GedcomFile } from '../../../services/groupTypes'
import { ManageTreeRow, ManageTreeRowSkeleton } from './ManageTreeRow'
import { useAllTreeGroups } from '../cets/AllTreeGroupsProvider'

// Combine processing files and groups into a single sorted list
type ListItem = { type: 'processing'; data: GedcomFile } | { type: 'group'; data: GroupSummary }

type Props = {
  processingFiles?: GedcomFile[]
  groups: GroupSummary[]
  failedFiles?: GedcomFileFailed[]
  isMobile: boolean
}

export default function ManageTreeList({ processingFiles, groups, failedFiles, isMobile }: Props): JSX.Element {
  const { uploadedFiles, loadingUploadedFiles } = useAllTreeGroups()

  if (loadingUploadedFiles) {
    return <ManageTreeListSkeleton isMobile={isMobile} />
  }

  const combinedItems: ListItem[] = [
    ...(processingFiles?.map((file) => ({ type: 'processing' as const, data: file })) || []),
    ...groups.map((group) => ({ type: 'group' as const, data: group })),
  ]

  // Sort by name (processingFile.title or group.name)
  combinedItems.sort((a, b) => {
    const nameA = a.type === 'processing' ? a.data.title : a.data.name
    const nameB = b.type === 'processing' ? b.data.title : b.data.name
    return nameA.toUpperCase().localeCompare(nameB.toUpperCase())
  })

  return (
    <List>
      {combinedItems.map((item, index) => {
        if (item.type === 'processing') {
          return (
            <ManageTreeRow
              key={item.data.fileName}
              processingFile={item.data}
              isMobile={isMobile}
              isLast={index === combinedItems.length - 1 && !failedFiles?.length}
            />
          )
        }
        const file = uploadedFiles.find((f) => f.treeId === item.data.treeIds?.[0])
        return (
          <ManageTreeRow
            key={item.data.id || file?.fileName || item.data.name}
            group={item.data}
            file={file}
            isMobile={isMobile}
            isLast={index === combinedItems.length - 1 && !failedFiles?.length}
          />
        )
      })}
      {failedFiles?.map((failedFile, index) => (
        <ManageTreeRow
          key={failedFile.fileName}
          failedFile={failedFile}
          isMobile={isMobile}
          isLast={index === failedFiles.length - 1}
        />
      ))}
    </List>
  )
}

export function ManageTreeListSkeleton({ isMobile }: { isMobile: boolean }): JSX.Element {
  return (
    <List>
      <ManageTreeRowSkeleton isMobile={isMobile} />
      <ManageTreeRowSkeleton isMobile={isMobile} />
      <ManageTreeRowSkeleton isMobile={isMobile} />
    </List>
  )
}
